import { useEffect, useMemo, useState } from "react";
import { FaEdit, FaPlus } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { openProtectedFile } from "../api/openProtectedFile";
import { isProjectManager } from "../auth/roles";
import DocumentMetadataFormModal from "../components/DocumentMetadataFormModal";

const categoryFilterOptions = ["All", "ERA", "ERAv3", "ERAv4", "EN&IEC", "EU", "Other"];

function getMetadataKey(fileName) {
  return String(fileName ?? "").trim().toLowerCase();
}

function getMetadataValue(metadata, field, fallback) {
  const value = metadata?.[field];
  return value ? value : fallback;
}

function getDisplayTitle(metadata, document) {
  return metadata?.title?.trim() || document.fileName;
}

export default function Docs({ user }) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [documents, setDocuments] = useState([]);
  const [documentMetadata, setDocumentMetadata] = useState([]);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [error, setError] = useState("");
  const isPm = isProjectManager(user);

  const loadDocuments = async () => {
    setIsLoading(true);
    setIsMetadataLoaded(false);
    try {
      const responses = await Promise.all([
        fetchWithAuth("/api/document-metadata/documents"),
        fetchWithAuth("/api/document-metadata")
      ]);

      for (const response of responses) {
        if (!response.ok) {
          const responseError = await response.json().catch(() => null);
          throw new Error(responseError?.error || "Failed to load documents");
        }
      }

      const [files, metadata] = await Promise.all(responses.map((response) => response.json()));
      setDocuments(files);
      setDocumentMetadata(metadata);
      setIsMetadataLoaded(true);
      setError("");
    } catch (err) {
      setDocuments([]);
      setDocumentMetadata([]);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const documentsWithMetadata = useMemo(() => {
    const metadataByFileName = new Map(documentMetadata
      .filter((metadata) => getMetadataKey(metadata.fileName))
      .map((metadata) => [getMetadataKey(metadata.fileName), metadata]));
    const fileNames = new Set(documents.map((document) => getMetadataKey(document.fileName)));
    const missingDocuments = documentMetadata
      .filter((metadata) => !metadata.fileName || !fileNames.has(getMetadataKey(metadata.fileName)))
      .map((metadata) => ({
        id: `metadata:${metadata.id}`,
        fileName: metadata.fileName || "",
        name: metadata.fileName?.replace(/\.[^.]+$/, "") || metadata.title || "",
        type: "",
        fileUrl: null,
        metadata
      }));

    return [...documents, ...missingDocuments].map((document) => {
      const metadata = document.metadata || metadataByFileName.get(getMetadataKey(document.fileName));

      return {
        ...document,
        title: getDisplayTitle(metadata, document),
        hasMetadataTitle: Boolean(metadata?.title?.trim() && document.fileName),
        description: getMetadataValue(metadata, "description", ""),
        category: metadata?.category === "EN" ? "EN&IEC" : getMetadataValue(metadata, "category", ""),
        referenceType: getMetadataValue(metadata, "referenceType", ""),
        metadata
      };
    }).sort((left, right) => left.name.localeCompare(
      right.name, undefined, { numeric: true, sensitivity: "base" }
    ));
  }, [documents, documentMetadata]);

  const filteredDocuments = useMemo(() => {
    const query = search.trim().toLowerCase();
    const selectedCategory = categoryFilter === "All" ? "" : categoryFilter;

    return documentsWithMetadata.filter((document) => {
      if (selectedCategory && document.category !== selectedCategory) {
        return false;
      }

      if (!query) return true;

      return (
        document.name.toLowerCase().includes(query) ||
        document.title.toLowerCase().includes(query) ||
        document.fileName.toLowerCase().includes(query) ||
        document.description.toLowerCase().includes(query) ||
        document.category.toLowerCase().includes(query) ||
        document.referenceType.toLowerCase().includes(query) ||
        document.type.toLowerCase().includes(query)
      );
    });
  }, [categoryFilter, documentsWithMetadata, search]);

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="mb-1">Docs</h3>
          <div className="text-muted">
            Reference documents and standards.
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {isPm && (
            <button
              type="button"
              className="btn btn-success d-inline-flex align-items-center gap-2"
              onClick={() => setIsCreatingDocument(true)}
              disabled={!isMetadataLoaded}
            >
              <FaPlus aria-hidden="true" />
              Add document
            </button>
          )}
          <select
            className="form-select"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            style={{ width: "130px" }}
            aria-label="Filter by category"
          >
            {categoryFilterOptions.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
              type="text"
              className="form-control pe-5"
              placeholder="Search docs..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          <span className="badge text-bg-secondary">
          Total: {filteredDocuments.length}
          </span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2">{error}</div>
      )}

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ ...stickyHeaderStyle, width: "280px" }}>Title</th>
              <th style={stickyHeaderStyle}>Description</th>
              <th style={{ ...stickyHeaderStyle, width: "170px" }}>Category</th>
              <th style={{ ...stickyHeaderStyle, width: "150px" }}>Reference type</th>
              <th style={{ ...stickyHeaderStyle, width: "110px" }}>File</th>
              {isPm && (
                <th style={{ ...stickyHeaderStyle, width: "90px" }}>Actions</th>
              )}
            </tr>
          </thead>

          <tbody>
            {filteredDocuments.length === 0 ? (
              <tr>
                <td colSpan={isPm ? 6 : 5} className="text-center text-muted py-4">
                  {isLoading ? "Loading documents..." : "No documents found."}
                </td>
              </tr>
            ) : filteredDocuments.map((document, index) => (
              <tr key={document.id} style={getRowStyle(index)}>
                <td className="fw-semibold">
                  <div>{document.title}</div>
                  {document.hasMetadataTitle && (
                    <div className="text-muted small mt-1">{document.fileName}</div>
                  )}
                </td>
                <td style={{ whiteSpace: "pre-wrap" }}>{document.description || <span className="text-muted">-</span>}</td>
                <td>{document.category || <span className="text-muted">-</span>}</td>
                <td>{document.referenceType || <span className="text-muted">-</span>}</td>
                <td>
                  {document.fileUrl && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => openProtectedFile(document.fileUrl, document.fileName)}
                    >
                      {document.type}
                    </button>
                  )}
                </td>
                {isPm && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setEditingDocument(document)}
                      disabled={!isMetadataLoaded}
                      title={isMetadataLoaded ? "Edit metadata" : "Loading metadata"}
                    >
                      <FaEdit />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DocumentMetadataFormModal
        show={isCreatingDocument || Boolean(editingDocument)}
        onClose={() => {
          setIsCreatingDocument(false);
          setEditingDocument(null);
        }}
        onSaved={loadDocuments}
        document={editingDocument}
      />
    </div>
  );
}
